import { useEffect, useMemo, useRef, useState } from 'react';
import {
  expandSheetForCustomTables,
  getExpandableRowInsertPlan
} from '../expandSheetForCustomTables';
import { createSheetFormulaEngine } from './createSheetFormulaEngine';
import { syncSheetInputsIntoEngine } from './syncSheetInputs';

const asSheetList = templateSheets => {
  if (Array.isArray(templateSheets)) {
    return templateSheets.filter(sheet => sheet?.name);
  }
  return templateSheets?.name ? [templateSheets] : [];
};

const expandKeyFromState = (templateSheets, documentState) =>
  JSON.stringify(
    asSheetList(templateSheets).map(sheet => [
      sheet.name,
      getExpandableRowInsertPlan(sheet.name, documentState).map(s => [s.field, s.extra])
    ])
  );

/**
 * Live HyperFormula workbook for all template sheets (cross-sheet refs work).
 * Rebuilds when expandable row counts change; re-syncs values on edit.
 */
export const useSheetFormulaEngine = ({
  templateSheets,
  templateSheet,
  expandedSheet,
  activeSheetName,
  documentState,
  pickedInventory,
  appChoices,
  docSource
}) => {
  const engineRef = useRef(null);
  const [revision, setRevision] = useState(0);

  const sheets = useMemo(
    () => asSheetList(templateSheets?.length ? templateSheets : templateSheet),
    [templateSheets, templateSheet]
  );

  const resolvedActiveName = activeSheetName || expandedSheet?.name || sheets[0]?.name;

  const expandKey = useMemo(
    () => expandKeyFromState(sheets, documentState),
    [sheets, documentState]
  );

  const expandedSheets = useMemo(
    () => sheets.map(sheet => expandSheetForCustomTables(sheet, documentState)),
    // Rebuild expands when geometry or sheet list changes; value sync is separate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sheets, expandKey]
  );

  // Rebuild workbook when template identity or expandable geometry changes.
  useEffect(() => {
    if (!sheets.length) {
      if (engineRef.current) {
        engineRef.current.destroy();
        engineRef.current = null;
      }
      setRevision(r => r + 1);
      return undefined;
    }

    if (engineRef.current) {
      engineRef.current.destroy();
    }
    const engine = createSheetFormulaEngine(sheets, documentState, resolvedActiveName);
    syncSheetInputsIntoEngine({
      engine,
      expandedSheets,
      documentState,
      pickedInventory,
      appChoices,
      docSource
    });
    engineRef.current = engine;
    setRevision(r => r + 1);

    return () => {
      engine.destroy();
      if (engineRef.current === engine) engineRef.current = null;
    };
    // expandedSheets is tied to expandKey; include expandKey explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheets, expandKey]);

  // Keep active sheet pointer in sync when the user switches tabs (no rebuild).
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine || !resolvedActiveName) return;
    engine.setActiveSheet(resolvedActiveName);
    setRevision(r => r + 1);
  }, [resolvedActiveName]);

  // Sync input values after edits without rebuilding row structure.
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;
    syncSheetInputsIntoEngine({
      engine,
      expandedSheets,
      documentState,
      pickedInventory,
      appChoices,
      docSource
    });
    setRevision(r => r + 1);
  }, [expandedSheets, documentState, pickedInventory, appChoices, docSource, expandKey]);

  return useMemo(
    () => ({
      revision,
      getFormulaDisplay: (row0, col0) =>
        engineRef.current?.getFormulaDisplay(row0, col0, resolvedActiveName) ?? '—',
      getFormulaText: (row0, col0) =>
        engineRef.current?.getFormulaText(row0, col0, resolvedActiveName) ?? '',
      hasFormula: (row0, col0) =>
        Boolean(engineRef.current?.hasFormula(row0, col0, resolvedActiveName))
    }),
    [revision, resolvedActiveName]
  );
};
